import React, { Fragment } from "react";
import { InfoCircleTwoTone } from "@ant-design/icons";
import { Table } from "antd";
import { useNavigate } from "react-router-dom";
import { formatKycCreatedOnDate } from "./kycHelpers";
import { highlightKycSearchText, renderKycSearchCell } from "./KycSearchHighlight";

const getSortOrder = (tableSorter, columnKey) => {
	if (tableSorter.field !== columnKey) {
		return null;
	}
	const o = String(tableSorter.order || "").toUpperCase();
	if (o === "ASC") {
		return "ascend";
	}
	if (o === "DESC") {
		return "descend";
	}
	return null;
};

const SERVER_SORTER = { compare: (_a, _b) => 0 };

const getCellValue = (record, key) => {
	const value = record?.[key];
	if (value !== undefined && value !== null && value !== "") {
		return value;
	}
	return "-";
};

const getRawValue = (record, key) => {
	const value = record?.[key];
	if (value !== undefined && value !== null && value !== "") {
		return value;
	}
	return "";
};

const formatDateValue = (value) => {
	if (value === undefined || value === null || value === "" || value === "-") {
		return "-";
	}
	return formatKycCreatedOnDate(value);
};

const formatRecordCount = (total, range) => {
	if (!total) {
		return "Showing 0 records";
	}

	return `Showing ${range[0]}-${range[1]} of ${total.toLocaleString()} records`;
};

const resolveQuotationId = (record) => getRawValue(record, "quotationId");

const resolveContactPersonId = (record) => getRawValue(record, "contactPersonId");

const KycQuotationTab = ({
	tabTableData,
	isLoading,
	tableSorter,
	OnhandleTableSortColumn,
	navigationContext = {},
	materialDetailsContext = null,
	partnerDetailsContext = null,
	emptyText = "No data found",
	tableSearchTerm = "",
}) => {
	const navigate = useNavigate();
	const renderRecordCell = (record, key) =>
		renderKycSearchCell(getCellValue(record, key), tableSearchTerm);
	const totalRecords = Number(tabTableData.totalRecords || 0);
	const currentPage = Number(tabTableData.currentPage || 1);
	const pageSize = Number(tabTableData.pageSize || 10);

	const handleTableAscDes = (pagination, filters, sorter) => {
		const s = Array.isArray(sorter) ? sorter[0] || {} : sorter || {};
		const sortOrder = s.order;
		const rawField = s.field;
		const sortField =
			rawField !== undefined && rawField !== null && rawField !== ""
				? Array.isArray(rawField)
					? rawField[0]
					: rawField
				: tableSorter.field;

		let nextOrder = tableSorter.order;
		if (sortOrder === "ascend") {
			nextOrder = "ASC";
		} else if (sortOrder === "descend") {
			nextOrder = "DESC";
		} else {
			const pageChanged =
				pagination.current !== tabTableData.currentPage ||
				(pagination.pageSize != null &&
					tabTableData.pageSize != null &&
					pagination.pageSize !== tabTableData.pageSize);
			if (!pageChanged && String(sortField) === String(tableSorter.field)) {
				const o = String(tableSorter.order || "").toUpperCase();
				nextOrder = o === "DESC" ? "ASC" : "DESC";
			}
		}

		OnhandleTableSortColumn({
			sortColumn: sortField,
			orderBy: nextOrder,
			currentPage: pagination.current,
			pageSize: pagination.pageSize,
		});
	};

	const handleQuotationDetailsClick = (event, record) => {
		if (event) {
			event.stopPropagation();
		}
		const quotationId = resolveQuotationId(record);
		if (!quotationId) {
			return;
		}

		if (materialDetailsContext?.productName) {
			navigate(`/kyc/quotation-details/${encodeURIComponent(quotationId)}`, {
				state: {
					quotationId,
					quotationNumber: getRawValue(record, "quotationNo"),
					returnSource: "material",
					materialProductName: materialDetailsContext.productName,
					materialName: materialDetailsContext.materialName || materialDetailsContext.productName,
					groupCompanyId: materialDetailsContext.groupCompanyId || "",
					groupCompanyName: materialDetailsContext.groupCompanyName || "",
					materialsCustomer: materialDetailsContext.materialsCustomer || "",
					accountName: materialDetailsContext.accountName || "",
					returnTab: materialDetailsContext.returnTab || "materials",
				},
			});
			return;
		}

		if (partnerDetailsContext?.crmGuid) {
			navigate(`/kyc/quotation-details/${encodeURIComponent(quotationId)}`, {
				state: {
					quotationId,
					quotationNumber: getRawValue(record, "quotationNo"),
					returnSource: "partner",
					partnerCrmGuid: partnerDetailsContext.crmGuid,
					customer: partnerDetailsContext.customer || "",
					groupCompanyId: partnerDetailsContext.groupCompanyId || "",
					groupCompanyName: partnerDetailsContext.groupCompanyName || "",
					accountName: partnerDetailsContext.accountName || "",
					materialsCustomer: partnerDetailsContext.materialsCustomer || "",
					returnTab: partnerDetailsContext.returnTab || "account",
				},
			});
			return;
		}

		navigate(`/kyc/quotation-details/${encodeURIComponent(quotationId)}`, {
			state: {
				quotationId,
				quotationNumber: getRawValue(record, "quotationNo"),
				groupCompanyId: navigationContext.groupCompanyId || "",
				groupCompanyName: navigationContext.groupCompanyName || "",
				returnTab: "quotation",
			},
		});
	};

	const handleContactPersonClick = (event, record) => {
		if (event) {
			event.stopPropagation();
		}
		const customerId = resolveContactPersonId(record);
		if (!customerId) {
			return;
		}

		if (materialDetailsContext?.productName) {
			navigate(`/kyc/quotation-contact/${encodeURIComponent(customerId)}`, {
				state: {
					primaryContactName: getRawValue(record, "contactPerson"),
					quotationNumber: getRawValue(record, "quotationNo"),
					returnSource: "material",
					materialProductName: materialDetailsContext.productName,
					materialName: materialDetailsContext.materialName || materialDetailsContext.productName,
					groupCompanyId: materialDetailsContext.groupCompanyId || "",
					groupCompanyName: materialDetailsContext.groupCompanyName || "",
					materialsCustomer: materialDetailsContext.materialsCustomer || "",
					accountName: materialDetailsContext.accountName || "",
					returnTab: materialDetailsContext.returnTab || "materials",
				},
			});
			return;
		}

		if (partnerDetailsContext?.crmGuid) {
			navigate(`/kyc/quotation-contact/${encodeURIComponent(customerId)}`, {
				state: {
					primaryContactName: getRawValue(record, "contactPerson"),
					quotationNumber: getRawValue(record, "quotationNo"),
					returnSource: "partner",
					partnerCrmGuid: partnerDetailsContext.crmGuid,
					customer: partnerDetailsContext.customer || "",
					groupCompanyId: partnerDetailsContext.groupCompanyId || "",
					groupCompanyName: partnerDetailsContext.groupCompanyName || "",
					accountName: partnerDetailsContext.accountName || "",
					materialsCustomer: partnerDetailsContext.materialsCustomer || "",
					returnTab: partnerDetailsContext.returnTab || "account",
				},
			});
			return;
		}

		navigate(`/kyc/quotation-contact/${encodeURIComponent(customerId)}`, {
			state: {
				primaryContactName: getRawValue(record, "contactPerson"),
				quotationNumber: getRawValue(record, "quotationNo"),
				groupCompanyId: navigationContext.groupCompanyId || "",
				groupCompanyName: navigationContext.groupCompanyName || "",
				returnTab: "quotation",
			},
		});
	};

	const tableColumns = [
		{
			title: "Quotation No.",
			dataIndex: "quotationNo",
			key: "quotationNo",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 160,
			sortOrder: getSortOrder(tableSorter, "quotationNo"),
			render: (_, record) => {
				const value = getCellValue(record, "quotationNo");
				const quotationId = resolveQuotationId(record);
				if (value === "-" || !quotationId) {
					return renderKycSearchCell(value, tableSearchTerm);
				}
				return (
					<span
						className="kyc-primary-contact-link"
						role="button"
						tabIndex={0}
						onClick={(event) => handleQuotationDetailsClick(event, record)}
						onKeyDown={(event) => {
							if (event.key === "Enter" || event.key === " ") {
								handleQuotationDetailsClick(event, record);
							}
						}}
					>
						{highlightKycSearchText(value, tableSearchTerm)}
					</span>
				);
			},
		},
		// {
		// 	title: "Order No.",
		// 	dataIndex: "orderNo",
		// 	key: "orderNo",
		// 	sorter: SERVER_SORTER,
		// 	showSorterTooltip: false,
		// 	width: 160,
		// 	sortOrder: getSortOrder(tableSorter, "orderNo"),
		// 	render: (_, record) => renderRecordCell(record, "orderNo"),
		// },
		{
			title: "Performa Invoice No.",
			dataIndex: "proformaInvoiceNo",
			key: "proformaInvoiceNo",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 190,
			sortOrder: getSortOrder(tableSorter, "proformaInvoiceNo"),
			render: (_, record) => renderRecordCell(record, "proformaInvoiceNo"),
		},
		{
			title: "Bill to Party",
			dataIndex: "billToParty",
			key: "billToParty",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 220,
			sortOrder: getSortOrder(tableSorter, "billToParty"),
			render: (_, record) => renderRecordCell(record, "billToParty"),
		},
		{
			title: "Sold to Party",
			dataIndex: "soldToParty",
			key: "soldToParty",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 220,
			sortOrder: getSortOrder(tableSorter, "soldToParty"),
			render: (_, record) => renderRecordCell(record, "soldToParty"),
		},
		{
			title: "Ship to Party",
			dataIndex: "shipToParty",
			key: "shipToParty",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 220,
			sortOrder: getSortOrder(tableSorter, "shipToParty"),
			render: (_, record) => renderRecordCell(record, "shipToParty"),
		},
		{
			title: "Contact Person",
			dataIndex: "contactPerson",
			key: "contactPerson",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 180,
			sortOrder: getSortOrder(tableSorter, "contactPerson"),
			render: (_, record) => {
				const value = getCellValue(record, "contactPerson");
				const contactPersonId = resolveContactPersonId(record);
				if (value === "-" || !contactPersonId) {
					return renderKycSearchCell(value, tableSearchTerm);
				}
				return (
					<span
						className="kyc-primary-contact-link"
						role="button"
						tabIndex={0}
						onClick={(event) => handleContactPersonClick(event, record)}
						onKeyDown={(event) => {
							if (event.key === "Enter" || event.key === " ") {
								handleContactPersonClick(event, record);
							}
						}}
					>
						{highlightKycSearchText(value, tableSearchTerm)}
					</span>
				);
			},
		},
		{
			title: "Sales Organization",
			dataIndex: "salesOrganization",
			key: "salesOrganization",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 200,
			sortOrder: getSortOrder(tableSorter, "salesOrganization"),
			render: (_, record) => renderRecordCell(record, "salesOrganization"),
		},
		{
			title: "Distribution Channel",
			dataIndex: "distributionChannel",
			key: "distributionChannel",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 190,
			sortOrder: getSortOrder(tableSorter, "distributionChannel"),
			render: (_, record) => renderRecordCell(record, "distributionChannel"),
		},
		{
			title: "Consignee Address",
			dataIndex: "consigneeAddress",
			key: "consigneeAddress",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 200,
			sortOrder: getSortOrder(tableSorter, "consigneeAddress"),
			render: (_, record) => renderRecordCell(record, "consigneeAddress"),
		},
		{
			title: "Customer Ref. Number",
			dataIndex: "customerRefNo",
			key: "customerRefNo",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 160,
			sortOrder: getSortOrder(tableSorter, "customerRefNo"),
			render: (_, record) => renderRecordCell(record, "customerRefNo"),
		},
		{
			title: "Customer Ref. Date",
			dataIndex: "customerRefDate",
			key: "customerRefDate",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 180,
			sortOrder: getSortOrder(tableSorter, "customerRefDate"),
			render: (_, record) =>
				renderKycSearchCell(formatDateValue(getRawValue(record, "customerRefDate")), tableSearchTerm),
		},
		{
			title: "Effective Untill",
			dataIndex: "effectiveUntil",
			key: "effectiveUntil",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 160,
			sortOrder: getSortOrder(tableSorter, "effectiveUntil"),
			render: (_, record) =>
				renderKycSearchCell(formatDateValue(getRawValue(record, "effectiveUntil")), tableSearchTerm),
		},
		{
			title: "Effective From",
			dataIndex: "effectiveFrom",
			key: "effectiveFrom",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 160,
			sortOrder: getSortOrder(tableSorter, "effectiveFrom"),
			render: (_, record) =>
				renderKycSearchCell(formatDateValue(getRawValue(record, "effectiveFrom")), tableSearchTerm),
		},
		{
			title: "Revision Id",
			dataIndex: "revisionId",
			key: "revisionId",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 130,
			sortOrder: getSortOrder(tableSorter, "revisionId"),
			render: (_, record) => renderRecordCell(record, "revisionId"),
		},
		{
			title: "Revision From",
			dataIndex: "revisedFrom",
			key: "revisedFrom",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 160,
			sortOrder: getSortOrder(tableSorter, "revisedFrom"),
			render: (_, record) => renderRecordCell(record, "revisedFrom"),
		},
		{
			title: "Performa Invoice Date",
			dataIndex: "proformaInvoiceDate",
			key: "proformaInvoiceDate",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 200,
			sortOrder: getSortOrder(tableSorter, "proformaInvoiceDate"),
			render: (_, record) =>
				renderKycSearchCell(formatDateValue(getRawValue(record, "proformaInvoiceDate")), tableSearchTerm),
		},
		{
			title: "Created On",
			dataIndex: "createdOn",
			key: "createdOn",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 150,
			sortOrder: getSortOrder(tableSorter, "createdOn"),
			render: (_, record) =>
				renderKycSearchCell(formatDateValue(getRawValue(record, "createdOn")), tableSearchTerm),
		},
		{
			title: "Owner",
			dataIndex: "owner",
			key: "owner",
			sorter: SERVER_SORTER,
			showSorterTooltip: false,
			width: 170,
			sortOrder: getSortOrder(tableSorter, "owner"),
			render: (_, record) => renderRecordCell(record, "owner"),
		},
		{
			title: "Action",
			key: "action",
			width: 90,
			fixed: "right",
			render: (_, record) => (
				<div
					className={`tbl_info_details ${resolveQuotationId(record) ? "" : "disabled"}`}
					onClick={(event) => handleQuotationDetailsClick(event, record)}
					role="presentation"
				>
					<InfoCircleTwoTone />
				</div>
			),
		},
	];

	return (
		<Fragment>
			<Table
				bordered
				scroll={{ x: 2400 }}
				className="logistic_tables kyc_logistic_tables kyc_quotation_tables"
				dataSource={tabTableData.rows}
				columns={tableColumns}
				sortDirections={["ascend", "descend"]}
				onChange={handleTableAscDes}
				rowKey={(record, index) =>
					resolveQuotationId(record) || getRawValue(record, "quotationNo") || index
				}
				locale={{ emptyText }}
				pagination={{
					total: totalRecords,
					current: currentPage,
					pageSize,
					responsive: true,
					showSizeChanger: false,
					showTotal: formatRecordCount,
				}}
				loading={isLoading}
			/>
		</Fragment>
	);
};

export default KycQuotationTab;
